import React from 'react';
import {Pie, Column} from '@ant-design/plots';
import {Card, Row, Col, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {formatString, formatValue, NoDataPlaceholder, truncateStr} from '../../../utils/helpers';

type UsersGraphProps = {
    onboardedUsersPerType: any,
    monthlyEarningsPerUserType: any,
};

interface DataItemOnboarded {
    user_type: string;
    numberOfUsers: number;
}

interface DataItemEarnings {
    user_type: string;
    onboardedUsersCommission: number
}

const FONT = 'Poppins';

const UsersGraph = ({onboardedUsersPerType, monthlyEarningsPerUserType}: UsersGraphProps) => {
    const onboardedUsersPerType_: DataItemOnboarded[] = onboardedUsersPerType

    const formattedData = onboardedUsersPerType_?.map((item: any) => ({
        ...item,
        'Users': item.numberOfUsers,
        'User type': formatString(item.user_type)
    }));

    const monthlyEarningsPerUserType_: DataItemEarnings[] = monthlyEarningsPerUserType.flatMap((item: {
        user_type: any;
        onboardedUsersCommission: any;
    }) => [
        {
            user_type: formatString(item.user_type),
            commission_type: 'Onboarded users commission',
            value: formatValue(item.onboardedUsersCommission)
        }
    ]);

    const config = {
        appendPadding: 10,
        data: formattedData || [],
        angleField: 'Users',
        colorField: 'User type',
        radius: 1,
        innerRadius: 0.6,
        label: {
            offset: '-30%',
            content: (d: any) => `${truncateStr(d['User type'], 10)}: ${d['Users']}`,
            style: {textAlign: 'center', fontSize: 12, fontFamily: FONT},
        },
        interactions: [{type: 'element-selected'}, {type: 'element-active'}],
        statistic: {
            title: {visible: true},
            content: {visible: true, style: {fontSize: 14, textAlign: 'center'}},
        },
        responsive: true,
        height: 320,
        autoFit: true,
        axis: {x: {labelFontFamily: FONT}, y: {labelFontFamily: FONT}},
        tooltip: {
            title: {style: {fontFamily: FONT}},
            itemName: {style: {fontFamily: FONT}},
            itemValue: {style: {fontFamily: FONT}}
        },
        legend: {position: 'bottom'}
    };

    const configEarnings = {
        data: monthlyEarningsPerUserType_ || [],
        isGroup: true,
        xField: 'user_type',
        yField: 'value',
        legend: {position: 'top'},
        colorField: 'commission_type',
        style: {inset: 5},
        responsive: true,
        height: 320,
        autoFit: true,
        axis: {
            x: {
                labelFontFamily: FONT,
                labelAutoHide: true,
                labelAutoRotate: true,
                labelRotate: -0.6,
                formatter: (v: string) => truncateStr(v, 10)
            },
            y: {labelFontFamily: FONT},
        },
        tooltip: {
            title: {style: {fontFamily: FONT}},
            itemName: {style: {fontFamily: FONT}},
            itemValue: {style: {fontFamily: FONT}}
        }
    };

    const MonthlyEarningsCustomizedCardTitle = () => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Earnings from onboarded users</span>
            <Tooltip
                title="Earnings from subscription fees and activity of the users you onboarded on Promotify categorized by type">
                <InfoCircleOutlined/>
            </Tooltip>
        </div>
    );

    const OnboardedUsersCustomizedCardTitle = () => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Onboarded users overview</span>
            <Tooltip title="Number of users you onboarded on Promotify categorized by type">
                <InfoCircleOutlined/>
            </Tooltip>
        </div>
    );

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                    <Card title={<MonthlyEarningsCustomizedCardTitle/>}>
                        {monthlyEarningsPerUserType_.length > 0 ? (
                            <Column {...configEarnings} />
                        ) : (
                            <NoDataPlaceholder message="No earning data from onboardings to display"/>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                    <Card title={<OnboardedUsersCustomizedCardTitle/>}>
                        {formattedData.length > 0 ? (
                            <Pie {...config} />
                        ) : (
                            <NoDataPlaceholder message="No onboarding data to display"/>
                        )}
                    </Card>
                </Col>
            </Row>
        </Card>
    );
};

export default UsersGraph;